/**
 * Figures in manuscripts and reports: an image file of the project placed in a section as
 *
 *   ![Caption text](bl://file/<uuid>)
 *
 * on a line of its own (a paragraph of its own). Figures are numbered in order of appearance across
 * the manuscript (Figure 1, 2, …) in the app and in every export, with the caption below. Only PNG,
 * JPEG, WebP and GIF images can be figures (never SVG), and the type is always read from the
 * image's own bytes, never from a name or a declared type.
 */

export interface ImageType {
  mime: "image/png" | "image/jpeg" | "image/webp" | "image/gif";
  ext: "png" | "jpg" | "webp" | "gif";
  label: "PNG" | "JPEG" | "WebP" | "GIF";
}

/** Largest image a figure can be (decoded), whatever the upload limit. */
export const FIGURE_MAX_BYTES = 15 * 1024 * 1024;

export const NOT_AN_IMAGE = "This text is not a valid PNG, JPEG, WebP or GIF image.";
export const NOT_AN_IMAGE_FILE = "This file is not a PNG, JPEG, WebP or GIF image.";

const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, to));

/** The image type from its first bytes (magic numbers), or null. */
export function detectImageType(b: Uint8Array): ImageType | null {
  if (b.length >= 8 && b[0] === 0x89 && ascii(b, 1, 4) === "PNG" && b[4] === 0x0d && b[5] === 0x0a && b[6] === 0x1a && b[7] === 0x0a) return { mime: "image/png", ext: "png", label: "PNG" };
  if (b.length >= 4 && b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return { mime: "image/jpeg", ext: "jpg", label: "JPEG" };
  if (b.length >= 10 && (ascii(b, 0, 6) === "GIF87a" || ascii(b, 0, 6) === "GIF89a")) return { mime: "image/gif", ext: "gif", label: "GIF" };
  if (b.length >= 16 && ascii(b, 0, 4) === "RIFF" && ascii(b, 8, 12) === "WEBP") return { mime: "image/webp", ext: "webp", label: "WebP" };
  return null;
}

export class ImageTextError extends Error {}

/**
 * The base64 of an image pasted as text: plain base64 (with or without line breaks, URL-safe or
 * not), or a `data:image/…;base64,…` URL (its declared type is ignored). Throws an ImageTextError
 * with a plain message when the text cannot be an image.
 */
export function imageTextBase64(text: string): string {
  let s = text.trim();
  const dataUrl = /^data:[^,]*,/i.exec(s);
  if (dataUrl) {
    if (!/;base64,$/i.test(dataUrl[0])) throw new ImageTextError(NOT_AN_IMAGE);
    s = s.slice(dataUrl[0].length);
  }
  s = s.replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
  if (!s) throw new ImageTextError("Paste the image text first.");
  const body = s.replace(/=+$/, "");
  if (!/^[A-Za-z0-9+/]+$/.test(body) || body.length % 4 === 1 || s.length - body.length > 2) throw new ImageTextError(NOT_AN_IMAGE);
  return body.padEnd(Math.ceil(body.length / 4) * 4, "=");
}

/** Bytes decoded from base64 that imageTextBase64 returned (the server decodes with Buffer instead). */
export function base64Bytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Decoded size of base64 text, without decoding it. */
export const base64Size = (b64: string) => Math.floor((b64.replace(/=+$/, "").length * 3) / 4);

const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const FIGURE_LINE_RE = new RegExp(`^ {0,3}!\\[((?:[^\\[\\]\\n\\\\]|\\\\.)*)\\]\\(\\s*bl://file/(${UUID})\\s*\\)\\s*$`);

/** `![Caption](bl://file/<uuid>)` alone on a line: its caption and file id. */
export function parseFigureLine(line: string): { caption: string; id: string } | null {
  const m = FIGURE_LINE_RE.exec(line);
  return m ? { caption: m[1].replace(/\\(.)/g, "$1").trim(), id: m[2].toLowerCase() } : null;
}

/** The Markdown line of a figure; the caption is kept to one line, without brackets. */
export function figureMarkdown(caption: string, fileId: string) {
  const c = caption
    .replace(/[\r\n]+/g, " ")
    .replace(/[[\]]/g, "")
    .replace(/\\+$/, "")
    .trim()
    .slice(0, 1000);
  return `![${c}](bl://file/${fileId.toLowerCase()})`;
}

export interface FoundFigure {
  id: string;
  caption: string;
  /** 1-based line number. */
  line: number;
}

/**
 * The figures of a Markdown text, in order: figure lines that stand as a paragraph of their own
 * (a blank line, a heading or the start before; a blank line, a heading or the end after), outside
 * code blocks and HTML comments.
 */
export function findFigures(markdown: string): FoundFigure[] {
  const lines = markdown.split("\n");
  const out: FoundFigure[] = [];
  const blank = (l: string | undefined) => l === undefined || !l.trim();
  const heading = (l: string | undefined) => l !== undefined && /^ {0,3}#{1,6}(\s|$)/.test(l);
  let fence: string | null = null;
  let comment = false;
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (fence) {
      if (new RegExp(`^ {0,3}${fence[0]}{${fence.length},}\\s*$`).test(l)) fence = null;
      continue;
    }
    if (comment) {
      if (l.includes("-->")) comment = false;
      continue;
    }
    const f = /^ {0,3}(`{3,}|~{3,})/.exec(l);
    if (f) {
      fence = f[1];
      continue;
    }
    if (/^ {0,3}<!--/.test(l) && !l.includes("-->")) {
      comment = true;
      continue;
    }
    const fig = parseFigureLine(l);
    if (fig && (blank(lines[i - 1]) || heading(lines[i - 1])) && (blank(lines[i + 1]) || heading(lines[i + 1]))) out.push({ ...fig, line: i + 1 });
  }
  return out;
}

/** End offset of each block (text between blank lines), in order. */
export function blockEnds(text: string): number[] {
  const ends: number[] = [];
  let pos = 0;
  let last = -1;
  for (const line of text.split("\n")) {
    if (line.trim()) last = pos + line.length;
    else if (last >= 0) {
      ends.push(last);
      last = -1;
    }
    pos += line.length + 1;
  }
  if (last >= 0) ends.push(last);
  return ends;
}

/** End of the block that contains `pos`, or of the block before it when `pos` is between blocks. */
export function blockEndAt(text: string, pos: number): number {
  let p = pos;
  while (p > 0 && /\s/.test(text[p - 1])) p--;
  for (const end of blockEnds(text)) if (end >= p) return end;
  return text.length;
}

/**
 * The change that puts a block (a figure line) at a block boundary `at`, with one blank line before
 * and after it: replaces text[from, to) by insert.
 */
export function blockInsertion(text: string, at: number, block: string): { from: number; to: number; insert: string } {
  const from = text.slice(0, at).replace(/\s+$/, "").length;
  const rest = text.slice(at);
  const lead = /^\s*/.exec(rest)![0];
  // Blank lines after it are replaced; the indentation of the next line is kept.
  const to = at + (lead.length === rest.length ? lead.length : lead.lastIndexOf("\n") + 1);
  return { from, to, insert: `${from > 0 ? "\n\n" : ""}${block}${to < text.length ? "\n\n" : "\n"}` };
}

export interface FigurePlacement {
  /** After the n-th paragraph (blocks separated by blank lines); 0 puts it first. */
  afterParagraph?: number;
  /** After the paragraph that contains this exact passage (it must occur once). */
  afterText?: string;
}

/** The text with the figure line placed: at the end, or after a given paragraph. Throws with a plain message. */
export function placeFigure(text: string, line: string, where: FigurePlacement = {}): string {
  let at = text.length;
  if (where.afterText !== undefined) {
    const i = text.indexOf(where.afterText);
    if (!where.afterText || i < 0) throw new Error("The passage to place the figure after is not in the section (copy it exactly).");
    if (text.indexOf(where.afterText, i + 1) >= 0) throw new Error("The passage to place the figure after occurs more than once; include more words so it is unique.");
    at = blockEndAt(text, i + where.afterText.length);
  } else if (where.afterParagraph !== undefined) {
    const ends = blockEnds(text);
    if (where.afterParagraph > ends.length) throw new Error(`The section has ${ends.length} paragraph${ends.length === 1 ? "" : "s"}: the figure cannot go after paragraph ${where.afterParagraph}.`);
    at = where.afterParagraph === 0 ? 0 : ends[where.afterParagraph - 1];
  }
  const c = blockInsertion(text, at, line);
  return text.slice(0, c.from) + c.insert + text.slice(c.to);
}
